import React, { useEffect, useState } from "react";
import { MdArrowDownward } from "react-icons/md";
import { IoMdCheckmark } from "react-icons/io";
import { GoDotFill } from "react-icons/go";
import { RxArrowRight, RxCaretDown, RxCaretUp, RxCross2 } from "react-icons/rx";
import { FaPencilAlt, FaTruck } from "react-icons/fa";
import { toast } from "react-toastify";
import apiClient from "../../services/apiClient";
import { IMAGE_PATH } from "../../utils/constants";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ReviewFormPopup from "./ReviewFormPopup";
import ReturnFormPopup from "./ReturnRequest";
import axios from "axios";

const OrderTracking = ({ order, onBack, user }) => {
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [showTrackingDetails, setShowTrackingDetails] = useState(false);

  // Fetch tracking data based on delivery partner
  useEffect(() => {
    const fetchTrackingData = async () => {
      if (!order?.tracking_id) return;
      
      setLoadingTracking(true);
      try {
        if (order.delivery_partner === 'ecart' && order.tracking_id) {
          // Fetch from Ekart API
          const response = await apiClient.get(`/tracking/${order.tracking_id}`);
          // Handle Ekart API structure
          const ekartData = response.data;
          if (ekartData && ekartData.track && Array.isArray(ekartData.track.details)) {
            setTrackingData({
              status: ekartData.track.status || order.status,
              estimated_delivery: ekartData.edd ? new Date(ekartData.edd).toISOString() : order.estimated_delivery_date,
              tracking_id: ekartData._id || order.tracking_id,
              delivery_partner: order.delivery_partner,
              history: ekartData.track.details.map(event => ({
                date: event.ctime ? new Date(event.ctime).toISOString() : null,
                status: event.status,
                location: event.location,
                desc: event.desc
              }))
            });
          } else {
            setTrackingData({
              status: order.status,
              estimated_delivery: order.estimated_delivery_date,
              tracking_id: order.tracking_id,
              delivery_partner: order.delivery_partner,
              history: order.tracking_history ? JSON.parse(order.tracking_history) : []
            });
          }
        } else {
          // For other delivery partners or if no API, use database data
          setTrackingData({
            status: order.status,
            estimated_delivery: order.estimated_delivery_date,
            tracking_id: order.tracking_id,
            delivery_partner: order.delivery_partner,
            history: order.tracking_history ? JSON.parse(order.tracking_history) : []
          });
        }
      } catch (error) {
        console.error("Error fetching tracking data:", error);
        // Fallback to database data if API fails
        setTrackingData({
          status: order.status,
          estimated_delivery: order.estimated_delivery_date,
          tracking_id: order.tracking_id,
          delivery_partner: order.delivery_partner,
          history: order.tracking_history ? JSON.parse(order.tracking_history) : []
        });
      } finally {
        setLoadingTracking(false);
      }
    };

    fetchTrackingData();
  }, [order]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determine current status for timeline
  const getStatusIndex = (status) => {
    const statusOrder = ['pending', 'paid', 'accepted', 'shipped', 'delivered', 'cancelled'];
    return statusOrder.indexOf(status.toLowerCase());
  };

  const currentStatus = order?.status || 'pending';
  const currentStatusIndex = getStatusIndex(currentStatus);

  // Timeline statuses
  const timelineSteps = [
    { status: 'Order Placed', key: 'pending' },
    { status: 'Paid', key: 'paid' },
    { status: 'Accepted', key: 'accepted' },
    { status: 'Shipped', key: 'shipped' },
    { status: 'Delivered', key: 'delivered' }
  ];

  // Get first product for display (or use combo)
  const getProductInfo = (item) => {
    if (item.product_type === 'combo' && item.combo) {
      return {
        name: item.combo.title,
        price: item.price,
        quantity: item.quantity,
        image: item.combo.image_path ? `${IMAGE_PATH}${item.combo.image_path}` : null,
        size: item.combo.products?.[0]?.size || 'N/A',
        isCombo: true,
        comboProducts: item.combo.products || [],
      };
    } else if (item.product) {
      return {
        name: item.product.name,
        price: item.price,
        quantity: item.quantity,
        image: item.product.media?.[0]?.file_path ? `${IMAGE_PATH}${item.product.media[0].file_path}` : null,
        size: item.product.size || 'N/A',
        isCombo: false
      };
    }
    
    return {
      name: 'Unknown Product',
      price: item.price,
      quantity: item.quantity,
      image: null,
      size: 'N/A',
      isCombo: false
    };
  };

  const firstProducts = order?.order_items?.map((item) => getProductInfo(item)) || [];

  const handleDownloadInvoice = () => {
    const doc = generateInvoice(order);
    doc.save(`Invoice_${order.id}.pdf`);
    toast.success(`Invoice for order #${order.id} downloaded`);
  };

  const generateInvoice = (order) => {
    const doc = new jsPDF({
      unit: 'mm',
      compress: true
    });

    doc.setFont('helvetica');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice No: XK00${order.id}`, 20, 26);
    doc.text(`Date: ${new Date(order.created_at).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    })}`, 20, 32);
    doc.text(`Status: ${order.status.toUpperCase()}`, 20, 38);
    doc.text(`Transaction Ref. No.: ${order.razorpay_payment_id}`,20,44)

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, 52);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let billToY = 58;
    const billToLines = [
      order.name,
      ...order.address.split(/\r?\n/),
      `${order.city}, ${order.state} - ${order.pincode}`,
      order.country,
      `Phone: ${order.phone}`,
      `Email: ${order.email}`
    ];

    billToLines.forEach(line => {
      doc.text(line, 20, billToY);
      billToY += 6;
    });

    doc.setDrawColor(200, 200, 200);
    doc.line(15, billToY + 4, 195, billToY + 4);

    const itemsData = order.order_items.map(item => [
      item.product_type === "combo" ? item.combo?.title : item.product?.name,
      item.product_type === "combo" ? 'N/A':item.product?.code || "N/A",
      item.quantity.toString(),
      `${parseFloat(item.price).toFixed(2)}`,
      `${(parseFloat(item.price) * item.quantity).toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: billToY + 10,
      head: [['Description', 'Code', 'Qty', 'Unit Price', 'Total']],
      body: itemsData,
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: 255,
        fontSize: 10,
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 70, halign: 'left' },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' }
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak'
      },
      margin: { left: 20, right: 20 }
    });

    const tableEndY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Xkei Wellness', 20, tableEndY);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yogifyY = tableEndY + 6;
    [
      'Karukutty, Angamaly, Kerala - 683576',
      'Phone: +91 8129057122',
    ].forEach(line => {
      doc.text(line, 20, yogifyY);
      yogifyY += 6;
    });

    doc.line(15, yogifyY + 4, 195, yogifyY + 4);

    const summaryY = yogifyY + 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    
    doc.text('Subtotal:', 140, summaryY);
    doc.text(`${parseFloat(order.total - order.shipping_fee).toFixed(2)} Rs.`, 200, summaryY, { align: 'right' });
    
    doc.text('Shipping:', 140, summaryY + 8);
    doc.text(`${parseFloat(order.shipping_fee).toFixed(2)} Rs.`, 200, summaryY + 8, { align: 'right' });
    
    doc.setFontSize(14);
    doc.text('Total:', 140, summaryY + 18);
    doc.text(`${parseFloat(order.total).toFixed(2)} Rs.`, 200, summaryY + 18, { align: 'right' });

    return doc;
  };

  // Render tracking information based on delivery partner
  const renderTrackingInfo = () => {
    if (!trackingData) {
      return <div className="TrackingInfo">No tracking information available</div>;
    }

    // Get the latest event from history
    const latestEvent = trackingData.history && trackingData.history.length > 0
      ? trackingData.history[trackingData.history.length - 1]
      : null;

    return (
      <div className="TrackingInfo">
        <div className="TrackingHeader" onClick={() => setShowTrackingDetails(!showTrackingDetails)}>
          <FaTruck className="TruckIcon" />
          <span>Tracking Information </span>
          <span className="ToggleIcon">
            {showTrackingDetails ? <RxCaretUp /> : <RxCaretDown />}
          </span>
        </div>
        {showTrackingDetails && (
          <div className="TrackingDetails">
            <div className="CurrentStatus">
              <strong>Status:</strong> {trackingData.status || order.status}
            </div>
            {trackingData.estimated_delivery && (
              <div className="EstimatedDelivery">
                <strong>Estimated Delivery:</strong> {formatDate(trackingData.estimated_delivery)}
              </div>
            )}
            {latestEvent && (
              <div className="LatestTrackingEvent">
                <strong>Latest Update:</strong>
                <div>
                  <span className="HistoryDate">{formatDateTime(latestEvent.date)}</span>{' '}
                  <span className="HistoryStatus">{latestEvent.status}</span>{' '}
                  <span className="HistoryLocation">{latestEvent.location || ''}</span>{' '}
                  {latestEvent.desc && <span className="HistoryDesc">{`- ${latestEvent.desc}`}</span>}
                </div>
              </div>
            )}
            {!latestEvent && (
              <div className="NoHistory">No tracking updates available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="OrderTraking">
      <div className="OrderTrakingMain">
        <div className="OrderTrackingBox">
          <div className="OrderTrackingHeader">
            <div className="OrderTrackingStatusHead">
              <p>Order Status</p>
            </div>
            <div className="BackToOrders" onClick={onBack}>
              <RxArrowRight className="BackArrow" />
              <span>Back to Orders</span>
            </div>
          </div>
          
          <div className="OrderIdInvoiceBox">
            <div className="OrderId">
              <p>Order Number:</p>
              <p>#{order?.id || 'N/A'}</p>
            </div>
            <div className="InvoiceBox">
              <p>Invoice:</p>
              <button onClick={handleDownloadInvoice}>
                <MdArrowDownward />
                <p>Download</p>
              </button>
            </div>
          </div>

          {/* Tracking Information */}
          {order?.tracking_id && renderTrackingInfo()}

          <div className="OrderStatusTimeline" data-status={currentStatus}>
            {timelineSteps.map((step, index) => {
              const isCompleted = index < currentStatusIndex;
              const isCurrent = index === currentStatusIndex;
              const isPending = index > currentStatusIndex;
              
              return (
                <div 
                  key={step.key} 
                  className={`TimelineItem ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isPending ? 'pending' : ''}`}
                >
                  <div className="TimelineDot">
                    {isCompleted ? <IoMdCheckmark /> : <GoDotFill />}
                  </div>
                  {index < timelineSteps.length - 1 && (
                    <div className={`TimelineConnector ${isCompleted ? 'completed' : 'pending'}`} />
                  )}
                  <div className="TimelineContent">
                    <p>{step.status}</p>
                    <p className="TimelineDate">
                      <span>{step.key === 'pending' && formatDate(order.created_at)}</span> 
                      {order?.delivered_date && step.key === 'delivered' && (
                        <span>{formatDate(order.delivered_date)}</span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="OrderDateAddressBox">
            <div className="OrderDate">
              <p className="label">Order Date:</p>
              <p className="value">{formatDateTime(order?.created_at)}</p>
            </div>
          </div>
          
          {firstProducts.length > 0 && 
            firstProducts.map((firstProduct,index) => (
              <div className="OrderDetailsBox" key={firstProduct?.id}>
                <div className="OrderImgDetailsBox">
                  <div className="OrderDetailsImg">
                    <img 
                      src={firstProduct?.image || '/images/placeholder-product.jpg'} 
                      alt={firstProduct?.name || 'Product'}
                      onError={(e) => {
                        e.target.src = '/images/placeholder-product.jpg';
                      }}
                    />
                  </div>
                  <div className="OrderProductDetailBox">
                    <div className="OrderProductName">
                      <p className="OrderProductTitle">{firstProduct?.name || 'Product'}</p>
                      <div className="ProductPrice">
                        <p className="OrderName">Price:</p>
                        <p className="offerPrice">₹{firstProduct?.price || order?.total || 'N/A'}</p>
                      </div>
                      <div className="ProductPrice">
                        <p className="OrderName">Size:</p>
                        <p className="offerPrice">{firstProduct?.size || 'N/A'}</p>
                      </div>
                      <div className="ProductPrice">
                        <p className="OrderName">Quantity:</p>
                        <p className="offerPrice">{firstProduct?.quantity || 1}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div 
                  className="ProgressBtn" 
                  style={index !== 0 ? { display: "none" } : {}}
                >
                  <button className={`status-${currentStatus}`}>
                    {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
                  </button>
                </div>
              </div>
            ))
          }
          
          <div className="ButtonBox">
            {/* Return Button */}
            <div className="ReturnBox">
              <button
                className="ReturnButton"
                onClick={() => setShowReturnForm(true)}
                disabled={order?.status !== 'delivered'}
              >
                Return Product
              </button>
            </div>

            {/* Review Button */}
            <div className="ReviewRateWrite">
              <div className="WriteReviewBox" onClick={() => setShowReviewForm(true)}>
                <p>Write a review</p>
                <FaPencilAlt className="PencilIcon" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Return Popup */}
      {showReturnForm && (
        <ReturnFormPopup 
          order={order} 
          user={user} 
          onClose={() => {
            setShowReturnForm(false);
            setSelectedProductId(null);
          }} 
        />
      )}

      {/* Review Popup */}
      {showReviewForm && (
        <ReviewFormPopup 
          order={order} 
          user={user} 
          onClose={() => {
            setShowReviewForm(false);
            setSelectedProductId(null);
          }} 
        />
      )}
    </div>
  );
};

export default OrderTracking;